// @ts-nocheck
import Vue from 'vue';
import { FIELD_MAPPINGS } from '@/config/defaultGemTypes';
export default Vue.extend({
    name: 'GemTooltip',
    props: {
        item: {
            type: Object,
            default: null
        },
        targetElement: {
            validator: (value) => value === null || value instanceof HTMLElement,
            default: null
        },
        gemType: {
            type: String,
            default: null
        },
        gemTypeConfig: {
            type: Object,
            default: null
        },
        visible: {
            type: Boolean,
            default: false
        },
        placement: {
            type: String,
            default: 'auto'
        },
        offset: {
            type: Number,
            default: 8
        },
        maxWidth: {
            type: Number,
            default: 320
        }
    },
    data() {
        return {
            position: null,
            isPositioned: false
        };
    },
    computed: {
        tooltipClasses() {
            return [
                'gem-tooltip',
                {
                    'positioned': this.isPositioned,
                    [`placement-${this.position?.placement}`]: this.position?.placement
                }
            ];
        },
        tooltipStyles() {
            if (!this.position) {
                return {
                    position: 'fixed',
                    visibility: 'hidden',
                    zIndex: 999999,
                    maxWidth: `${this.maxWidth}px`
                };
            }
            return {
                position: 'fixed',
                top: `${this.position.top}px`,
                left: `${this.position.left}px`,
                zIndex: 999999,
                maxWidth: `${this.maxWidth}px`,
                visibility: 'visible'
            };
        },
        arrowClasses() {
            const placement = this.position?.placement || 'right'; // Default fallback
            return [
                'gem-tooltip-arrow',
                `gem-arrow-${placement}` // Always have a directional class
            ];
        },
        arrowStyles() {
            return {
                '--arrow-color': 'var(--gem-tooltip-bg)'
            };
        },
        arrowInlineStyles() {
            // Minimal inline styles - CSS handles everything else
            return {};
        },
        gemColorStyles() {
            if (!this.gemTypeConfig && !this.gemType)
                return {};
            // Import stores and configs
            const { themeStore } = require('@/stores/themeStore');
            const { GEM_THEME_COLORS, getDynamicGemThemeColor } = require('@/config/defaultGemTypes');
            const theme = themeStore.theme || 'light';
            // Get theme colors based on gem type
            let themeColors = null;
            if (this.gemType && GEM_THEME_COLORS[this.gemType]) {
                themeColors = GEM_THEME_COLORS[this.gemType][theme];
            }
            else if (this.gemType) {
                // For dynamic types, use hash-based color selection
                let hash = 0;
                for (let i = 0; i < this.gemType.length; i++) {
                    const char = this.gemType.charCodeAt(i);
                    hash = ((hash << 5) - hash) + char;
                    hash = hash & hash;
                }
                const colorIndex = Math.abs(hash) % 20;
                themeColors = getDynamicGemThemeColor(colorIndex)[theme];
            }
            else if (this.gemTypeConfig?.color) {
                // Fallback to static colors
                return {
                    '--gem-accent-color': this.gemTypeConfig.color,
                    '--gem-accent-bg': this.gemTypeConfig.backgroundColor || this.gemTypeConfig.color + '20'
                };
            }
            if (themeColors) {
                return {
                    '--gem-accent-color': themeColors.color,
                    '--gem-accent-bg': themeColors.backgroundColor
                };
            }
            return {};
        },
        customFields() {
            if (!this.item)
                return [];
            const standardFields = new Set([
                'id', 'title', 'text', 'quote', 'summary', 'next_step',
                'citation_id', 'title_org_language',
                // Exclude source session metadata fields
                '_source_session', '_source_session_name', '_source_project', '_source_category',
                ...Object.values(FIELD_MAPPINGS).flat()
            ]);
            return Object.entries(this.item)
                .filter(([key, value]) => !standardFields.has(key) && value != null && value !== '')
                .map(([key, value]) => ({
                key,
                label: this.formatFieldLabel(key),
                value
            }));
        }
    },
    watch: {
        visible: {
            handler(newVisible) {
                if (newVisible && this.targetElement) {
                    // Double nextTick to ensure DOM is ready
                    this.$nextTick(() => {
                        this.$nextTick(() => {
                            this.updatePosition();
                        });
                    });
                }
                else {
                    this.position = null;
                    this.isPositioned = false;
                }
            },
            immediate: true
        },
        targetElement: {
            handler() {
                if (this.visible && this.targetElement) {
                    // Double nextTick to ensure DOM is ready
                    this.$nextTick(() => {
                        this.$nextTick(() => {
                            this.updatePosition();
                        });
                    });
                }
            },
            immediate: true
        }
    },
    mounted() {
        // Add window resize listener
        window.addEventListener('resize', this.handleResize);
        window.addEventListener('scroll', this.handleScroll, true);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.handleResize);
        window.removeEventListener('scroll', this.handleScroll, true);
    },
    methods: {
        getItemTitle(item) {
            for (const field of FIELD_MAPPINGS.title) {
                if (item[field]) {
                    return item[field];
                }
            }
            return `Item #${item.id || 'N/A'}`;
        },
        getItemOriginalTitle(item) {
            for (const field of FIELD_MAPPINGS.originalLanguage) {
                if (item[field] && item[field] !== this.getItemTitle(item)) {
                    return item[field];
                }
            }
            return null;
        },
        formatFieldLabel(key) {
            return key
                .replace(/([A-Z])/g, ' $1')
                .replace(/[_-]/g, ' ')
                .replace(/\b\w/g, l => l.toUpperCase())
                .trim();
        },
        getSourcePath() {
            if (!this.item)
                return null;
            const item = this.item;
            const sourceProject = item._source_project;
            const sourceCategory = item._source_category;
            const sourceSessionName = item._source_session_name;
            // Only show source path for project-level gems (when source metadata is available)
            if (sourceProject && sourceCategory && sourceSessionName) {
                return `<span style="color: var(--color-text-secondary);">${sourceProject} > ${sourceCategory} > ${sourceSessionName}</span>`;
            }
            return null;
        },
        getGemEmoji() {
            if (!this.gemType)
                return '💎';
            const { DEFAULT_GEM_TYPE_CONFIGS } = require('@/config/defaultGemTypes');
            const config = DEFAULT_GEM_TYPE_CONFIGS[this.gemType];
            return config?.emoji || config?.icon || '💎';
        },
        getDisplayName() {
            if (!this.gemType)
                return 'Gem';
            const { DEFAULT_GEM_TYPE_CONFIGS } = require('@/config/defaultGemTypes');
            const config = DEFAULT_GEM_TYPE_CONFIGS[this.gemType];
            if (config?.displayName) {
                return config.displayName;
            }
            // Format the type name if no display name exists
            return this.gemType
                .split(/[-_]/)
                .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(' ');
        },
        updatePosition() {
            if (!this.targetElement || !this.$refs.tooltip) {
                return;
            }
            const tooltip = this.$refs.tooltip;
            const target = this.targetElement;
            // Get target element bounds
            const targetRect = target.getBoundingClientRect();
            const tooltipRect = tooltip.getBoundingClientRect();
            // Get viewport dimensions
            const viewport = {
                width: window.innerWidth,
                height: window.innerHeight
            };
            // Calculate optimal placement
            const placement = this.calculateOptimalPlacement(targetRect, tooltipRect, viewport);
            const coords = this.calculateCoordinates(targetRect, tooltipRect, placement);
            this.position = {
                top: coords.top,
                left: coords.left,
                placement
            };
            this.isPositioned = true;
        },
        calculateOptimalPlacement(targetRect, tooltipRect, viewport) {
            if (this.placement !== 'auto') {
                return this.placement;
            }
            // Calculate available space in each direction
            const spaces = {
                top: targetRect.top,
                bottom: viewport.height - targetRect.bottom,
                left: targetRect.left,
                right: viewport.width - targetRect.right
            };
            // PREFER RIGHT FIRST, then left, then top/bottom
            if (spaces.right >= tooltipRect.width + this.offset)
                return 'right';
            if (spaces.left >= tooltipRect.width + this.offset)
                return 'left';
            if (spaces.top >= tooltipRect.height + this.offset)
                return 'top';
            if (spaces.bottom >= tooltipRect.height + this.offset)
                return 'bottom';
            // Fallback to direction with most space
            const maxSpace = Math.max(...Object.values(spaces));
            return Object.keys(spaces).find(key => spaces[key] === maxSpace) || 'right';
        },
        calculateCoordinates(targetRect, tooltipRect, placement) {
            let top = 0;
            let left = 0;
            switch (placement) {
                case 'top':
                    top = targetRect.top - tooltipRect.height - this.offset;
                    left = targetRect.left + (targetRect.width - tooltipRect.width) / 2;
                    break;
                case 'bottom':
                    top = targetRect.bottom + this.offset;
                    left = targetRect.left + (targetRect.width - tooltipRect.width) / 2;
                    break;
                case 'left':
                    top = targetRect.top + (targetRect.height - tooltipRect.height) / 2;
                    left = targetRect.left - tooltipRect.width - this.offset;
                    break;
                case 'right':
                    top = targetRect.top + (targetRect.height - tooltipRect.height) / 2;
                    left = targetRect.right + this.offset;
                    break;
            }
            // Ensure tooltip stays within viewport bounds
            const viewport = {
                width: window.innerWidth,
                height: window.innerHeight
            };
            top = Math.max(8, Math.min(top, viewport.height - tooltipRect.height - 8));
            left = Math.max(8, Math.min(left, viewport.width - tooltipRect.width - 8));
            return { top, left };
        },
        handleResize() {
            if (this.visible) {
                this.updatePosition();
            }
        },
        handleScroll() {
            if (this.visible) {
                this.updatePosition();
            }
        },
        // Arrow positioning is now handled entirely by CSS classes
    }
});
